import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'cts_desktop_notif_optin';

/**
 * Manages the browser Notification API + a user opt-in flag.
 *
 * Returned API:
 *   permission: 'default' | 'granted' | 'denied' | 'unsupported'
 *   enabled:    boolean — user opt-in preference (localStorage backed)
 *   setEnabled: (next: boolean) => void
 *   request():  Promise<NotificationPermission | 'unsupported'>
 *               Triggers Notification.requestPermission() if currently 'default'.
 *   show({ title, body, tag, onClick }):
 *               No-ops when not granted, not opted-in, or while the tab is
 *               foregrounded (document.visibilityState === 'visible').
 */
export function useDesktopNotifications() {
  const supported = typeof window !== 'undefined' && 'Notification' in window;
  const [permission, setPermission] = useState(() =>
    supported ? Notification.permission : 'unsupported'
  );
  const [enabled, setEnabledState] = useState(() => readOptIn());

  // Cross-tab sync of the opt-in flag.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setEnabledState(readOptIn());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setEnabled = useCallback((next) => {
    const bool = !!next;
    try {
      window.localStorage.setItem(STORAGE_KEY, bool ? '1' : '0');
    } catch {
      // ignore storage failures (private mode, quota, etc.)
    }
    setEnabledState(bool);
  }, []);

  const request = useCallback(async () => {
    if (!supported) return 'unsupported';
    if (Notification.permission !== 'default') {
      setPermission(Notification.permission);
      return Notification.permission;
    }
    try {
      const res = await Notification.requestPermission();
      setPermission(res);
      return res;
    } catch (err) {
      console.warn('[desktop-notifications] requestPermission failed', err);
      return Notification.permission;
    }
  }, [supported]);

  const show = useCallback(({ title, body, tag, onClick } = {}) => {
    if (!supported) return null;
    if (!enabled) return null;
    if (Notification.permission !== 'granted') return null;
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') return null;
    try {
      const n = new Notification(title ?? 'Notification', { body, tag });
      if (typeof onClick === 'function') {
        n.onclick = onClick;
      }
      return n;
    } catch (err) {
      console.warn('[desktop-notifications] show failed', err);
      return null;
    }
  }, [supported, enabled]);

  return { permission, enabled, setEnabled, request, show };
}

function readOptIn() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}
